import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "./shared";
import LeadDrawer from "./LeadDrawer";
import { Modal, Input, SelectField } from "./CrmLeads";
import CalendarSyncPanel from "./CalendarSyncPanel";
import { ChevronLeft, ChevronRight, Plus, Check, Trash2, Pencil, Calendar as CalIcon } from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const EVENT_TYPES = [
    ["prima_visita", "Prima Visita"],
    ["seconda_visita", "Seconda Visita"],
    ["proposta_acquisto", "Proposta d'Acquisto"],
    ["incarico_vendita", "Incarico di Vendita"],
    ["rogito", "Rogito / Atto Notarile"],
    ["valutazione", "Valutazione Immobile"],
    ["appuntamento_ufficio", "Appuntamento in Ufficio"],
    ["promemoria_personale", "Promemoria Personale"],
];
const EVENT_TYPE_LABELS = Object.fromEntries(EVENT_TYPES);
const dayKey = (iso) => { try { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; } catch { return ""; } };

export default function CrmCalendar() {
    const today = new Date();
    const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
    const [data, setData] = useState({ appointments: [], events: [] });
    const [extEvents, setExtEvents] = useState([]);
    const [selectedDay, setSelectedDay] = useState(dayKey(today.toISOString()));
    const [showNew, setShowNew] = useState(false);
    const [openLead, setOpenLead] = useState(null);
    const [editingEvent, setEditingEvent] = useState(null);
    const [agents, setAgents] = useState([]);

    const load = useCallback(async () => {
        try {
            const [r, a] = await Promise.all([api.get("/crm/calendar", { params: { days_back: 60, days_forward: 180 } }), api.get("/crm/agents")]);
            setData(r.data);
            setAgents(a.data.agents || []);
        } catch (e) { console.error(e); }
        try {
            const x = await api.get("/crm/external-events", { params: { days_back: 60, days_forward: 180 } });
            setExtEvents(x.data.items || []);
        } catch (e) { console.error(e); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const byDay = {};
    for (const a of data.appointments) (byDay[dayKey(a.start_at)] = byDay[dayKey(a.start_at)] || []).push({ ...a, _type: "appt" });
    for (const e of data.events) (byDay[dayKey(e.start_at)] = byDay[dayKey(e.start_at)] || []).push({ ...e, _type: "event" });
    const localGoogleIds = new Set(data.events.map((event) => event.google_event_id).filter(Boolean));
    for (const x of extEvents) {
        const mirrored = [...localGoogleIds].some((id) => x.uid === id || String(x.uid || "").startsWith(`${id}@`));
        if (!mirrored) (byDay[dayKey(x.start_at)] = byDay[dayKey(x.start_at)] || []).push({ ...x, _type: "ext" });
    }

    const first = new Date(cursor.y, cursor.m, 1);
    const startDow = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells = [...Array(startDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
    const todayKey = dayKey(today.toISOString());

    const decide = async (id, action) => {
        try { await api.put(`/appointments/${id}/decide`, { action }); await load(); }
        catch (e) { alert(e?.response?.data?.detail || "Errore"); }
    };
    const toggleDone = async (ev) => { await api.put(`/crm/events/${ev.id}`, { done: !ev.done }); load(); };
    const removeEvent = async (ev) => {
        const { data: result } = await api.delete(`/crm/events/${ev.id}`);
        if (result.google_sync_status === "error") toast.warning("Evento eliminato da VendiDirect; rimozione Google in attesa di recupero.");
        load();
    };

    const dayItems = (byDay[selectedDay] || []).sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));

    return (
        <div data-testid="crm-calendar-tab" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-3">
                <CalendarSyncPanel />
            </div>
            <div className="lg:col-span-3">
                <RescheduleRequests appointments={data.appointments} onChanged={load} />
            </div>
            <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <button data-testid="cal-prev" onClick={() => setCursor((c) => c.m === 0 ? { y: c.y - 1, m: 11 } : { ...c, m: c.m - 1 })} className="p-2 border border-white/15 hover:border-gold text-ink-100"><ChevronLeft size={15} /></button>
                        <h3 className="font-serif text-2xl text-ink-50 w-52 text-center">{MONTHS[cursor.m]} {cursor.y}</h3>
                        <button data-testid="cal-next" onClick={() => setCursor((c) => c.m === 11 ? { y: c.y + 1, m: 0 } : { ...c, m: c.m + 1 })} className="p-2 border border-white/15 hover:border-gold text-ink-100"><ChevronRight size={15} /></button>
                    </div>
                    <button data-testid="cal-new-event" onClick={() => setShowNew(true)} className="cta-gold px-4 py-2.5 text-xs flex items-center gap-2"><Plus size={14} /> Nuovo impegno / visita</button>
                </div>
                <div className="grid grid-cols-7 border-l border-t border-white/10">
                    {DOW.map((d) => <div key={d} className="text-[10px] uppercase tracking-wider text-ink-400 text-center py-2 border-r border-b border-white/10 bg-white/5">{d}</div>)}
                    {cells.map((d, i) => {
                        if (d === null) return <div key={`e${i}`} className="min-h-[76px] border-r border-b border-white/10 bg-ink-950/50" />;
                        const key = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                        const items = byDay[key] || [];
                        const isToday = key === todayKey;
                        const isSel = key === selectedDay;
                        return (
                            <button key={key} data-testid={`cal-day-${key}`} onClick={() => setSelectedDay(key)}
                                className={`no-pill min-h-[76px] border-r border-b border-white/10 p-1.5 text-left align-top transition-colors ${isSel ? "bg-gold/10" : "hover:bg-white/5"}`}>
                                <span className={`text-xs ${isToday ? "text-gold font-bold" : "text-ink-100"}`}>{d}</span>
                                <div className="mt-1 space-y-0.5">
                                    {items.slice(0, 3).map((it, j) => (
                                        <div key={it.id || `${it._type}-${j}`} className={`text-[9px] px-1 py-0.5 truncate ${it._type === "appt" ? (it.status === "confirmed" ? "bg-green-900/40 text-green-200" : "bg-amber-900/40 text-amber-200") : it._type === "ext" ? "bg-blue-900/40 text-blue-200 border-l border-blue-400/50" : it.done ? "bg-ink-900 text-ink-500 line-through" : "bg-purple-900/40 text-purple-200"}`}>
                                            {it._type === "appt" ? `Visita ${it.client_name || ""}` : it.title}
                                        </div>
                                    ))}
                                    {items.length > 3 && <div className="text-[9px] text-ink-400">+{items.length - 3}</div>}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Day detail */}
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <CalIcon size={15} className="text-gold" />
                    <h3 className="font-serif text-xl text-ink-50">{new Date(selectedDay + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</h3>
                </div>
                <div data-testid="cal-day-detail" className="space-y-4">
                    {dayItems.length === 0 && <div className="text-sm text-ink-400 border border-white/10 p-5 bg-ink-900/30">Nessun impegno.</div>}
                    {dayItems.map((it) => (
                        <div key={it.id} className="vd-card vd-lead-row p-3.5">
                            {it._type === "appt" ? (
                                <>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-ink-50 font-medium">Visita · {it.client_name}</span>
                                        <span className={`text-[9px] uppercase px-1.5 py-0.5 ${it.status === "confirmed" ? "bg-green-900/30 text-green-300" : it.status === "pending" ? "bg-amber-900/30 text-amber-300" : "bg-ink-900 text-ink-400"}`}>{it.status}</span>
                                    </div>
                                    <div className="text-[11px] text-ink-400 mt-1">{it.property_title} · {fmtDate(it.start_at)}</div>
                                    <div className="flex gap-2 mt-2.5">
                                        {it.lead_id && <button data-testid={`cal-open-lead-${it.id}`} onClick={() => setOpenLead(it.lead_id)} className="text-[11px] text-gold hover:underline">→ Scheda cliente</button>}
                                        {it.status === "pending" && (
                                            <>
                                                <button data-testid={`cal-confirm-${it.id}`} onClick={() => decide(it.id, "confirm")} className="text-[11px] px-2 py-1 border border-green-700/40 text-green-300 hover:bg-green-900/20">Conferma</button>
                                                <button data-testid={`cal-decline-${it.id}`} onClick={() => decide(it.id, "decline")} className="text-[11px] px-2 py-1 border border-red-700/40 text-red-300 hover:bg-red-900/20">Rifiuta</button>
                                            </>
                                        )}
                                    </div>
                                </>
                            ) : it._type === "ext" ? (
                                <>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm text-ink-50 font-medium">{it.title}</span>
                                        <span className="text-[9px] uppercase px-1.5 py-0.5 bg-blue-900/30 text-blue-300 shrink-0">esterno</span>
                                    </div>
                                    <div className="text-[11px] text-ink-400 mt-1">{it.calendar_name} · {fmtDate(it.start_at)}</div>
                                </>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className={`text-sm font-medium ${it.done ? "text-ink-500 line-through" : "text-ink-50"}`}>{it.title}</span>
                                        <div className="flex gap-1.5 shrink-0">
                                            <button data-testid={`cal-edit-${it.id}`} onClick={() => setEditingEvent(it)} title="Modifica" className="p-1 border border-white/15 text-ink-400 hover:text-gold"><Pencil size={12} /></button>
                                            <button data-testid={`cal-done-${it.id}`} onClick={() => toggleDone(it)} title="Completa" className={`p-1 border ${it.done ? "border-green-700/40 text-green-300" : "border-white/15 text-ink-400 hover:text-green-300"}`}><Check size={12} /></button>
                                            <button data-testid={`cal-del-${it.id}`} onClick={() => removeEvent(it)} className="p-1 border border-white/15 text-ink-400 hover:text-red-300"><Trash2 size={12} /></button>
                                        </div>
                                    </div>
                                    <div className="text-[11px] text-ink-400 mt-1">
                                        {EVENT_TYPE_LABELS[it.kind] || (it.kind === "promemoria" ? "Promemoria" : "Attività")} · {fmtDate(it.start_at)}
                                        {it.agent && <span className="text-purple-200"> · 👤 {it.agent}</span>}
                                    </div>
                                    {it.notes && <p data-testid={`cal-notes-${it.id}`} className="text-[11px] text-ink-300 mt-2 whitespace-pre-wrap">{it.notes}</p>}
                                    {it.google_sync_status === "error" && <p data-testid={`cal-google-sync-error-${it.id}`} className="text-[10px] text-amber-300 mt-2">Google: sincronizzazione in attesa</p>}
                                    {it.lead_id && <button onClick={() => setOpenLead(it.lead_id)} className="text-[11px] text-gold hover:underline mt-1.5">→ {it.lead_name || "Scheda cliente"}</button>}
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {openLead && <LeadDrawer leadId={openLead} agents={agents} onClose={() => setOpenLead(null)} onChanged={load} />}
            {showNew && <NewEventModal agents={agents} defaultDay={selectedDay} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
            {editingEvent && <NewEventModal event={editingEvent} agents={agents} defaultDay={selectedDay} onClose={() => setEditingEvent(null)} onCreated={() => { setEditingEvent(null); load(); }} />}
        </div>
    );
}

function NewEventModal({ event, agents, defaultDay, onClose, onCreated }) {
    const [form, setForm] = useState({
        title: event?.title || "",
        date: event?.start_at?.slice(0, 10) || defaultDay,
        time: event?.start_at?.slice(11, 16) || "10:00",
        kind: event?.kind || "promemoria_personale",
        lead_id: event?.lead_id || "",
        agent: event?.agent || "",
        notes: event?.notes || "",
    });
    const [leads, setLeads] = useState([]);
    const [busy, setBusy] = useState(false);
    const needsLead = form.kind !== "promemoria_personale";
    useEffect(() => { api.get("/crm/leads").then((r) => setLeads(r.data.items || [])).catch(() => {}); }, []);
    const submit = async () => {
        if (!form.title.trim()) return;
        setBusy(true);
        try {
            const payload = { title: form.title, start_at: `${form.date}T${form.time}:00`, notes: form.notes };
            const { data } = event
                ? await api.put(`/crm/events/${event.id}`, payload)
                : await api.post("/crm/events", { ...payload, kind: form.kind, lead_id: form.lead_id || null, agent: form.agent || null });
            if (data.google_sync_status === "error") {
                toast.warning("Evento salvato in VendiDirect; sincronizzazione Google in attesa di recupero.");
            } else if (!event && needsLead) {
                toast.success(data.email_sent ? "Creato: email di conferma inviata al cliente." : "Creato (email non inviata: il cliente non ha un indirizzo email).");
            } else if (event) {
                toast.success("Appuntamento aggiornato");
            }
            onCreated();
        } catch (e) { alert(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };
    return (
        <Modal title={event ? "Modifica appuntamento" : "Nuovo appuntamento"} onClose={onClose}>
            <div className="space-y-3">
                <Input testid="event-title" label="Titolo *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Data</label>
                        <input data-testid="event-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                            className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none" />
                    </div>
                    <div>
                        <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Ora</label>
                        <input data-testid="event-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                            className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none" />
                    </div>
                </div>
                {!event && <div className="grid grid-cols-2 gap-3">
                    <SelectField label="Tipo" value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} testid="event-kind"
                        options={EVENT_TYPES} />
                    <SelectField label="Agente" value={form.agent} onChange={(v) => setForm({ ...form, agent: v })}
                        options={[["", "—"], ...agents.map((a) => [a, a])]} />
                </div>}
                {!event && <SelectField label={needsLead ? "Cliente / lead collegato *" : "Cliente / lead collegato"} value={form.lead_id} onChange={(v) => setForm({ ...form, lead_id: v })}
                    options={[["", "—"], ...leads.map((l) => [l.id, `${l.name}${l.property_title ? ` · ${l.property_title}` : ""}`])]} />
                }
                <div>
                    <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Note</label>
                    <textarea data-testid="event-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="4" maxLength="4000" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none resize-y" />
                </div>
                {!event && needsLead && (
                    <p data-testid="visit-note" className="text-[11px] text-gold/90 border border-gold/30 bg-gold/5 px-3 py-2">
                        Il cliente riceverà subito un'email di conferma appuntamento con data, ora e pulsante "Aggiungi al calendario".
                    </p>
                )}
                <button data-testid="event-submit" onClick={submit} disabled={busy || !form.title.trim() || (needsLead && !form.lead_id)} className="cta-gold w-full py-3 text-sm disabled:opacity-40">
                    {busy ? (event ? "Salvataggio..." : "Creazione...") : event ? "Salva modifiche" : needsLead ? "Crea e invia conferma al cliente" : "Crea"}
                </button>
            </div>
        </Modal>
    );
}

function RescheduleRequests({ appointments, onChanged }) {
    const reqs = (appointments || []).filter((a) => a.status === "reschedule_requested");
    if (reqs.length === 0) return null;
    return (
        <div data-testid="reschedule-requests" className="border border-amber-700/40 bg-amber-900/10 p-4">
            <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-3">
                🔄 Richieste di modifica orario dal cliente ({reqs.length})
            </div>
            <div className="space-y-3">
                {reqs.map((a) => <RescheduleCard key={a.id} a={a} onChanged={onChanged} />)}
            </div>
        </div>
    );
}

function RescheduleCard({ a, onChanged }) {
    const [date, setDate] = useState((a.start_at || "").slice(0, 10));
    const [time, setTime] = useState((a.start_at || "").slice(11, 16) || "10:00");
    const [busy, setBusy] = useState(false);
    const when = fmtDate(a.start_at);

    const act = async (action) => {
        setBusy(true);
        try {
            const body = { action };
            if (action === "reschedule") body.start_at = `${date}T${time}:00`;
            const r = await api.put(`/crm/appointments/${a.id}/manage`, body);
            if (action !== "cancel") {
                window.alert(r.data.email_sent ? "Fatto: il cliente è stato avvisato via email." : "Fatto (email non inviata: il cliente non ha un indirizzo email).");
            }
            onChanged();
        } catch (e) {
            window.alert(e?.response?.data?.detail || "Operazione non riuscita");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid={`reschedule-card-${a.id}`} className="vd-card vd-lead-row p-3.5">
            <div className="flex items-center gap-2 flex-wrap text-sm">
                <span className="text-ink-50 font-medium">{a.client_name}</span>
                <span className="text-ink-400 text-xs">· {a.property_title} · attuale: {when}</span>
            </div>
            {a.reschedule_message && (
                <div data-testid={`reschedule-msg-${a.id}`} className="mt-2 text-xs text-amber-200 border-l-2 border-amber-500/60 pl-2">
                    «{a.reschedule_message}»
                </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
                <input data-testid={`resched-date-${a.id}`} type="date" value={date} onChange={(e) => setDate(e.target.value)}
                    className="bg-transparent border border-white/15 px-2 py-1.5 text-xs text-ink-50 outline-none focus:border-gold" />
                <input data-testid={`resched-time-${a.id}`} type="time" value={time} onChange={(e) => setTime(e.target.value)}
                    className="bg-transparent border border-white/15 px-2 py-1.5 text-xs text-ink-50 outline-none focus:border-gold" />
                <button data-testid={`resched-apply-${a.id}`} onClick={() => act("reschedule")} disabled={busy || !date || !time}
                    className="cta-gold px-3 py-1.5 text-[11px] disabled:opacity-40">Sposta e avvisa il cliente</button>
                <button data-testid={`resched-keep-${a.id}`} onClick={() => act("keep")} disabled={busy}
                    className="px-3 py-1.5 text-[11px] border border-green-700/40 text-green-300 hover:bg-green-900/20">Mantieni orario</button>
                <button data-testid={`resched-cancel-${a.id}`} onClick={() => { if (window.confirm("Annullare la visita? Il cliente verrà avvisato via email.")) act("cancel"); }} disabled={busy}
                    className="px-3 py-1.5 text-[11px] border border-red-700/40 text-red-300 hover:bg-red-900/20">Annulla visita</button>
            </div>
        </div>
    );
}
